import React, {Fragment, useState, useRef, useEffect, useLayoutEffect, useContext} from 'react'
import styled from '@emotion/styled';
import pocketHitsImage from './nl-signup-illustration.jpg';
import {color, breakpoint, font} from '../../constants';
import {validateEmail} from '../../helpers/validation-helpers'
import {registerEmail} from '../../api'
import {PageContext} from '../PocketHitsShared/page-context'
import analyticsTriggers from '../../analytics/triggers';
import VisibilitySensor from '../../../Components/VisibilitySensor';

const ERROR_MSG = "Invalid email address";
const EMPTY_ERROR = "";

const PocketHitsCTA = styled.div`
  background-color: ${color.white100};
  color: ${color.gray80};
  padding: 24px 20px 24px 0;
  box-sizing: border-box;
  display: flex;
  font-family: ${font.GRAPHIK};
  border: solid 1px ${color.gray40};
  justify-content: center;
  width: 100%;
  margin: 24px auto 48px;
  
  & {
    input[type=email] {
      font-size: 14px;
    }
  }
  
  ${breakpoint.atMediumWidth} {
    justify-content: center;
    padding: 24px 20px;
    margin-top: 0;
  }
  
  ${breakpoint.atSmallWidth} {
    padding: 16px;
    min-height: 269px;
  }
`

const SignupImage = styled.div`
  background-image: url(${pocketHitsImage});
  width: 214px;
  height: 171px;
  flex: 0 0 auto;
  margin-right: 10px;
  background-size: contain;
  
  @media (max-width: 800px) {
    display: none;
  }
`

const Header = styled.h1`
  font-family: ${font.DOYLE};
  font-size: 20px;
  color: ${color.gray100};
  margin: 0 0 12px;
  font-weight: 400;
  line-height: 157%;
  
  & span {
    display: block;
  }
  
  ${breakpoint.atSmallWidth} {
    font-size: 20px;
    line-height: 140%;
    
    & span {
      display: none;
    }
  }
`

const SecondaryHeader = styled(Header)`
  color: ${color.gray80};
  display: none;
  
  ${breakpoint.atSmallWidth} {
    display: block;
    font-size: 13px;
    margin-bottom: 30px;
  }
`

const Description = styled.p`
  font-size: 16px;
  font-weight: 300;
  line-height: 150%;
  margin: 0 0 16px;
  
  ${breakpoint.atSmallWidth} {
    font-size: 15px;
  }
`

const Form = styled.form`
  font-size: 14px;
  display: flex;
  
  ${breakpoint.atSmallWidth} {
    flex-direction: column;
  }
`;

const Input = styled.input`
  height: 40px;
  background-color: ${color.white100};
  border: 1px solid ${color.gray20};
  box-sizing: border-box;
  border-radius: 4px;
  margin-right: 8px;
  padding: 12px 20px;
  flex: 1;
  line-height: 125%;
  
  &:active, &:focus {
    border-color: {(props) => props.isDisabled && ${color.gray100}};
    color: {(props) => props.isDisabled && ${color.gray100}};
  }
  &:invalid {
    box-shadow: none !important;
  }
  &:invalid:not(:focus):not(:placeholder-shown) {
    color: ${color.coral80};
  }
  
  ${breakpoint.atSmallWidth} {
    width: 100%;
    margin-bottom: 8px;
  }
`

const ErrorInput = styled(Input)`
  color: ${color.coral80};
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 487px;
  
  ${breakpoint.atSmallWidth} {
    padding: 0;
  }
`

const Submit = styled.button`
  background-color: ${color.coral80};
  color: ${color.white100};
  height: 40px;
  border-radius: 4px;
  padding: 11px 24px 13px;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  box-sizing: border-box;
  border: solid 1px ${color.coral80};
  
  &:hover, &:active, &:focus {
    background-color: ${color.oneoff.pocketHitsSubmitHover};
    border: solid 1px ${color.oneoff.pocketHitsSubmitHover};
    cursor: pointer;
    transition-duration: 200ms;
  }
`

const SubmitProcessing = styled(Submit)`
    background-color: ${color.oneoff.pocketHitsSubmitHover};
    pointer-events: none;
    
  &:hover, &:active, &:focus {
    cursor: not-allowed;
  }
`

const requestNewsletterSignup = (email, updateSuccess, updateError, updateRequestStatus, signedParams, updateSignedParams) => {
  // don't hit API with an empty string, or improper email
  if (!validateEmail(email)) return updateError(ERROR_MSG);

  analyticsTriggers.pocketHitsSignupSubmit();

  updateRequestStatus(true);

  registerEmail({
    email,
    ...signedParams,
    utm_source: 'syndication',
    utm_campaign: 'article-footer'
  }, (refreshedSignedParams) => {
    analyticsTriggers.pocketHitsSignupSubmitSuccess();
    updateSuccess(true);
    updateSignedParams(refreshedSignedParams);
  }, () => {
    analyticsTriggers.pocketHitsSignupSubmitError();
    updateError(ERROR_MSG);
  }, () => updateRequestStatus(false))
}

const handleInputFocus = (e) => {
  analyticsTriggers.pocketHitsSignupFieldFocus();
}

const handleInputVisible = (e) => {
  analyticsTriggers.pocketHitsSignupFieldView();
}

const DefaultState = ({updateSuccess, updateError, error, signedParams, updateSignedParams}) => {
  const [emailInput, updateEmail] = useState('');
  const [requestInFlight, updateRequestStatus] = useState(false);
  const [isEditing, toggleEditing] = useState(false);
  const inputRef = useRef(null);

  // used to pass the input focus when switching from rendering the error input to default input,
  // so users don't have to click the input twice to clear the error and be able to edit
  useLayoutEffect(() => {
    if (isEditing) {
      inputRef.current.focus();
    }

    return () => {
      toggleEditing(false);
    }
  }, [isEditing]);

  const submitHandler = e => {
    e.preventDefault();

    toggleEditing(false);
    requestNewsletterSignup(emailInput, updateSuccess, updateError, updateRequestStatus, signedParams, updateSignedParams);
  }

  return (
      <Fragment>
        <Header>Get fascinating stories delivered to
          you&nbsp;daily. <span>Pocket curates the best of the web.</span></Header>
        <SecondaryHeader>Pocket curates the best of the web.</SecondaryHeader>
        <Form noValidate onSubmit={submitHandler}>
          {error
              ? <ErrorInput type="email" name="email" placeholder="Your email address" value={error}
                            onChange={e => updateEmail(e.target.value)} onClick={() => {
                updateError(EMPTY_ERROR);
                toggleEditing(true);
              }}/>
              : <VisibilitySensor onVisible={handleInputVisible}>
                <Input
                    ref={inputRef}
                    type="email"
                    name="email"
                    disabled={requestInFlight}
                    isDisabled={requestInFlight}
                    placeholder="Your email address"
                    value={emailInput}
                    onChange={e => updateEmail(e.target.value)}
                    onFocus={handleInputFocus}
                 />
              </VisibilitySensor>}
          {requestInFlight
            ? <SubmitProcessing>Working on it</SubmitProcessing>
            : <Submit type="submit">Get Pocket’s newsletter</Submit>
          }
        </Form>
      </Fragment>
  )
}

const SuccessState = () => (
    <Fragment>
      <Header>Almost done! Please check your&nbsp;inbox.</Header>
      <Description>We’ve sent you an email to confirm your subscription. You’re one step away from a daily dose of the best stories from around the&nbsp;web.</Description>
    </Fragment>
)

export default ({initialSuccess = false, initialError = ''}) => {
  const {signedParams, updateSignedParams} = useContext(PageContext);
  const [isSuccess, updateSuccess] = useState(initialSuccess);
  const [error, updateError] = useState(initialError);

  return <PocketHitsCTA>
    <SignupImage/>
    <Body>
      {isSuccess
          ? <SuccessState/>
          : <DefaultState error={error} updateError={updateError} updateSuccess={updateSuccess}
                          signedParams={signedParams} updateSignedParams={updateSignedParams}/>}
    </Body>
  </PocketHitsCTA>
}
